import { useRecoilState } from 'recoil'
import { videoAnnotationsState, videoProgressState, ocrState } from '@/store'
import AnnotationsPanel from '@/components/AnnotationsView/panel/AnnotationsPanel'
import Player from '@/components/AnnotationsView/Player'
import Editor from '@/components/AnnotationsView/Editor'
import { useState, useEffect } from 'react'
import analytics from '@/utils/analytics'
import { demoVideo, getRandomID } from '@/utils/index'
import dynamic from 'next/dynamic'
import confetti from 'canvas-confetti'
import AppSection from '@/components/app/AppSection'
import Button from '@/components/Button'
import toast from 'react-hot-toast'
import ActionHeader from './ActionHeader'
import useOnEsc from '@/utils/useOnEsc'

const Tour = dynamic(() => import('reactour'), {
  ssr: false,
})

export default function Demo() {
  const [currentStep, setCurrentStep] = useState(0)
  const [showDemo, setShowDemo] = useState(true)
  const isDemo = true
  const isReadOnly = false
  const [video, setVideo] = useState('')
  const [annotations, setAnnotations] = useRecoilState(videoAnnotationsState)
  const [demoAnnotation, setDemoAnnotation] = useState(null)
  const [progress] = useRecoilState(videoProgressState)
  const [isDragging, setIsDragging] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [ocr] = useRecoilState(ocrState)

  useOnEsc({ show: showDemo, setShow: setShowDemo })

  useEffect(() => {
    if (!localStorage.getItem('demoVideo')) {
      localStorage.setItem('demoVideo', JSON.stringify(demoVideo))
    }

    const demoVideoData = JSON.parse(localStorage.getItem('demoVideo') || {})
    setVideo(demoVideoData)
  }, [])

  const steps = [
    {
      selector: '.editor-content',
      content: function step0() {
        return (
          <div>
            This is the section where you&apos;re going to write your notes.
            <br />
            <br />
            To create a note you need to write at least a title or a body.
            <br />
            <br />
            Go ahead and write your first one.
            <br />
            <br />
            E.g: My first note.
          </div>
        )
      },
    },
    {
      selector: '.time-picker',
      content: function step1() {
        return (
          <div>
            When you start typing, the note gets associated with the current
            time of the video.
            <br />
            <br />
            This is done automatically but don&apos;t worry, you can change it
            to any time you want.
          </div>
        )
      },
      stepInteraction: false,
      action: (node) => {
        if (node) {
          const inputTime = document.querySelector('.time-picker input')
          const time = inputTime && inputTime.getAttribute('title')
          analytics.track('(Demo) Step 2', { time })
        } else {
          setDemoAnnotation({
            _id: getRandomID(),
            video: video._id,
            title: 'My first note',
            content: 'My comments',
            start: (progress && progress.playedSeconds) || 0,
          })
          setCurrentStep(0)
          setTimeout(() => {
            const input = document.querySelector('input.time-picker')
            if (input) {
              input.scrollIntoViewIfNeeded()
            }
            setCurrentStep(1)
          }, 200)
        }
      },
    },
    {
      selector: '.save-button',
      content: function step2() {
        return <div>Click this button to save your note.</div>
      },
      action: () => {
        analytics.track('(Demo) Step 3')
      },
    },
    {
      selector: '.annotations-list .annotation',
      content: function step3() {
        return (
          <div>
            Congrats! 👏
            <br />
            <br />
            You&apos;ve created your first note.
            <br />
            <br />
            Click it to jump on that time in the video or edit it.
            <br />
            <br />
            <Button
              onClick={() => {
                analytics.track('(Demo) Finish')
                setShowDemo(false)
                setCurrentStep(0)
              }}
            >
              Finish!
            </Button>
          </div>
        )
      },
      action: (node) => {
        if (node) {
          analytics.track('(Demo) Step 4')

          const showPanelButton = document.querySelector(
            '.annotations-menu-button button'
          )

          if (showPanelButton) {
            showPanelButton.click()
          }

          const end = Date.now() + 0.5 * 1000

          const colors = ['#33334f', '#6381f3']

          ;(function frame() {
            confetti({
              particleCount: 2,
              angle: 60,
              spread: 55,
              origin: { x: 0 },
              colors: colors,
            })
            confetti({
              particleCount: 2,
              angle: 120,
              spread: 55,
              origin: { x: 1 },
              colors: colors,
            })

            if (Date.now() < end) {
              requestAnimationFrame(frame)
            }
          })()
        } else {
          if (demoAnnotation) {
            setAnnotations([demoAnnotation])
            setDemoAnnotation(null)
            setCurrentStep(2)
            setTimeout(() => {
              setCurrentStep(3)
            }, 200)
            return
          }

          const saveButton = document.querySelector('.save-button')
          if (saveButton) {
            saveButton.click()
            setCurrentStep(3)
          }
        }
      },
    },
  ]

  function shouldCreate() {
    if (!showDemo) {
      return true
    }

    if (currentStep === 2) {
      setCurrentStep(3)
      return true
    } else if (currentStep === 3) {
      return true
    } else {
      toast.warn('Wait, we will create the note later')
      return false
    }
  }

  return (
    <AppSection
      title="Interactive Demo"
      description="Interactive demo of Annotate.tv. Take a real look of how you can take notes on videos."
      canonical="/demo"
      wrapContent={false}
      isLoading={!video}
    >
      <>
        {ocr && (
          <div className="absolute top-0 left-0 right-0 bottom-0 bg-white z-40" />
        )}
        <div className="flex">
          <Tour
            closeWithMask={false}
            steps={steps}
            isOpen={showDemo}
            disableKeyboardNavigation={true}
            disableFocusLock={true}
            getCurrentStep={(curr) => setCurrentStep(curr)}
            goToStep={currentStep}
            onRequestClose={() => setShowDemo(false)}
            rounded={10}
            startAt={0}
          />

          <AnnotationsPanel
            {...{ setAnnotations, annotations, video, isReadOnly, isDemo }}
          />

          <div className="flex flex-col w-full overflow-auto h-screen">
            <ActionHeader
              showTutorial={() => setShowDemo(true)}
              annotations={annotations}
            />
            <Player
              {...{
                video,
                isDemo,
                isDragging,
                setIsDragging,
                isHovering,
                setIsHovering,
              }}
            />
            <Editor
              {...{
                demoAnnotation,
                setAnnotations,
                video,
                isDemo,
                isReadOnly,
                annotations: annotations,
                shouldCreate,
                isDragging,
                isHovering,
                setShowDemo,
              }}
            />
          </div>
        </div>
      </>
    </AppSection>
  )
}
